import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Box, Button, IconButton, Stack } from '@mui/material';
import _ from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { ApiResultModal } from '../../../components/modal/ApiResultHandler';
import {
  PrimaryButton,
  PrimaryOutlinedButton,
} from '../../../components/style/ButtonStyle';
import { CommonDivider } from '../../../components/style/CommonStyle';
import {
  DetailInfoText,
  FormLabelText,
  FormTitle,
  MobileTitle,
} from '../../../components/style/TextStyle';
import { useAuthAxios } from '../../../hooks/useAuthAxios';
import { useLogout } from '../../../hooks/useLogout';
import useResponsive from '../../../hooks/useResponsive';
import { useYupValidationResolver } from '../../../hooks/useYupValidationResolver';
import { responsiveMargin } from '../../../utils/enum';
import { TextInput } from '../MapPage/FacilityFormPage/CommonForm';
import { defaultValue, userSchema } from '../UserPage/Common/DefaultAndSchema';
import UserInputs from '../UserPage/Common/UserInputs';

export default function EditMyAccountPage() {
  const mdUp = useResponsive('up', 'md');

  const [getMeResult] = useAuthAxios(
    {
      method: 'GET',
      url: `/api/v1/user/who-am-i`,
    },
    { manual: false },
  );

  const me = _.get(getMeResult, 'response.data');

  const resolver = useYupValidationResolver(
    Yup.object().shape({
      ...userSchema,
      currentPassword: Yup.string().when('newPassword', {
        is: value => !!value,
        then: schema => schema.min(1, '이전 비밀번호를 입력해주세요'),
        otherwise: schema => schema.nullable(),
      }),
      newPassword: Yup.string().when({
        is: value => !!value,
        then: schema =>
          schema
            .matches(
              /.*[a-zA-Z]+.*/,
              '비밀번호는 영문, 숫자, 특수문자를 모두 입력해주세요',
            )
            .matches(
              /.*[0-9]+.*/,
              '비밀번호는 영문, 숫자, 특수문자를 모두 입력해주세요',
            )
            .matches(
              /.*[!@#$%&*.()-+=_|\\]+.*/,
              '비밀번호는 영문, 숫자, 특수문자를 모두 입력해주세요',
            )
            .min(5, '비밀번호는 5자리 이상입니다')
            .max(200, '비밀번호는 최대 200자리입니다'),
        otherwise: schema => schema.nullable(),
      }),
      recheckPassword: Yup.string().when('newPassword', {
        is: value => !!value,
        then: schema =>
          schema.oneOf(
            [Yup.ref('newPassword')],
            '비밀번호가 일치하지 않습니다.',
          ),
        otherwise: schema => schema.nullable(),
      }),
    }),
  );

  const form = useForm<any>({
    mode: 'onTouched',
    resolver,
    defaultValues: {
      ...defaultValue,
      currentPassword: undefined,
      newPassword: undefined,
      recheckPassword: undefined,
    },
    shouldUnregister: false,
  });

  const setValue = form.setValue;
  useEffect(() => {
    if (me) {
      setValue('name', me?.name);
      setValue('userId', me?.userId);
      setValue('email', me?.email);
      setValue('phone', me?.phone);
      setValue('administrativeRegion', me?.administrativeRegion);
      setValue('departmentId', me?.departmentId);
      setValue('role', me?.role);
      setValue('createdAt', me?.createdAt);
    }
  }, [me, setValue]);

  const [putPasswordResult, putPassword] = useAuthAxios(
    {
      method: 'PUT',
      url: `/api/v1/user/myInfo`,
    },
    { manual: true },
  );
  const onSubmit = form.handleSubmit(function (values) {
    const payload = {
      administrativeRegion: values.administrativeRegion,
      createdAt: values.createdAt,
      departmentId: values.departmentId,
      email: values.email,
      name: values.name,
      phone: values.phone,
      role: values.role,
      userId: values.userId,
      currentPassword:
        values.currentPassword === '' ? undefined : values.currentPassword,
      newPassword: values.newPassword === '' ? undefined : values.newPassword,
    };
    putPassword({ data: payload });
  });

  return (
    <Stack mx={responsiveMargin} spacing={5} my={{ xs: 2, md: 10 }}>
      {mdUp ? (
        <DesktopHeader
          onSubmit={onSubmit}
          putPasswordResult={putPasswordResult}
        />
      ) : (
        <MobileHeader
          onSubmit={onSubmit}
          putPasswordResult={putPasswordResult}
        />
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ w: '100%' }}
        spacing={5}
      >
        <Stack spacing={2} sx={{ flex: 1 }}>
          <FormLabelText>
            ID<span>*</span>
          </FormLabelText>
          <DetailInfoText>{me?.id || '-'}</DetailInfoText>
        </Stack>
        <Stack sx={{ flex: 1 }}>
          <TextInput
            form={form}
            isRequired={true}
            label="이름"
            fieldPath="name"
            placeholder="예시 : 홍길동"
          />
        </Stack>
      </Stack>
      <UserInputs form={form} />
      <Stack sx={{ flex: 1 }}>
        <TextInput
          form={form}
          isRequired={true}
          label="현재 비밀번호"
          fieldPath="currentPassword"
          placeholder="현재 비밀번호를 입력하세요."
          type="password"
        />
      </Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ w: '100%' }}
        spacing={5}
      >
        <Stack sx={{ flex: 1 }}>
          <TextInput
            form={form}
            isRequired={true}
            label="변경할 비밀번호"
            fieldPath="newPassword"
            placeholder="새 비밀번호를 입력하세요."
            type="password"
          />
        </Stack>
        <Stack sx={{ flex: 1 }}>
          <TextInput
            form={form}
            isRequired={true}
            label="비밀번호 확인"
            fieldPath="recheckPassword"
            placeholder="새 비밀번호를 다시 입력하세요."
            type="password"
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

function DesktopHeader({ onSubmit, putPasswordResult }: any) {
  const { logout } = useLogout();
  const navigate = useNavigate();
  return (
    <>
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate(`/users/me`)}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>내 계정 정보 수정</FormTitle>
        <Box />
        <Stack direction="row" spacing={1}>
          <PrimaryOutlinedButton
            sx={{ width: 130, height: 40 }}
            onClick={() => navigate(`/users/me`)}
          >
            취소
          </PrimaryOutlinedButton>
          <PrimaryButton
            sx={{ width: 130, height: 40 }}
            onClick={() => {
              onSubmit();
            }}
          >
            수정
          </PrimaryButton>
          <EditMyAccountResultModal
            putPasswordResult={putPasswordResult}
            logout={logout}
          />
        </Stack>
      </Stack>
      <CommonDivider />
    </>
  );
}

function MobileHeader({ onSubmit, putPasswordResult }: any) {
  const navigate = useNavigate();
  const { logout } = useLogout();
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <IconButton
        sx={{ width: 'fit-content', justifyContent: 'flex-start' }}
        onClick={() => navigate(`/users/me`)}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <MobileTitle>내 계정 정보 수정</MobileTitle>
      <Button
        variant="text"
        onClick={() => {
          onSubmit();
        }}
      >
        저장
      </Button>
      <EditMyAccountResultModal
        putPasswordResult={putPasswordResult}
        logout={logout}
      />
    </Stack>
  );
}

function EditMyAccountResultModal({ putPasswordResult, logout }: any) {
  return (
    <ApiResultModal
      apiResult={putPasswordResult}
      successCode={200}
      successTitle={
        <>
          내 정보가 변경되어 로그아웃 되었습니다.
          <br />
          다시 로그인 후 이용해주시기 바랍니다.
        </>
      }
      successContent={``}
      successConfirmText="확인"
      onSuccessClose={() => {
        logout();
      }}
      failTitle="관리자에게 문의해주세요."
      ErrorCodeTable={{
        USER_NOT_FOUND: {
          title: (
            <>
              입력하신 정보와 일치하는 정보가 없습니다. <br />
              확인 후 다시 입력해주세요.
            </>
          ),
        },
        INVALID_PW: {
          title: '비밀번호를 확인해주세요.',
        },
      }}
    ></ApiResultModal>
  );
}
